import { FC, useEffect, useState } from 'react'
import { Checkbox, Modal } from 'antd'
import cn from 'classnames'
import cclasses from '@pages/Admin/CRM/styles/checkbox.module.css'
import jclasses from '@pages/Admin/CRM/styles/jsmodal.module.css'
import classes from '../../index.module.css'
import { classApi } from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import Button from '@UI/New/Button'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import { InfoCircleOutlined } from '@ant-design/icons'
import Preloader from '@UI/Preloader'
import ChatAudienceRows, {
  ChatAudience,
  ChatRow,
  createChatRow,
  DEFAULT_CHAT_ROLE,
  normalizeChatRole,
} from '@common/ChatAudienceRows'

interface ClassAutoChatsProps {
  classId?: number | null
}

type Audience = ChatAudience

type RowsByAudience = Record<Audience, ChatRow[]>

interface SaveConfirmation {
  showStudents: boolean
  showParents: boolean
  addExistingStudents: boolean
  addExistingParents: boolean
}

const AUDIENCES: Audience[] = ['student', 'parent', 'staff']

const EMPTY_ROWS: RowsByAudience = { student: [], parent: [], staff: [] }

/** Строки настроек из ответа backend. Старый формат (списки чатов + одна роль) — fallback. */
const parseRows = (data: any): RowsByAudience => {
  const rows: RowsByAudience = { student: [], parent: [], staff: [] }

  if (Array.isArray(data?.settings)) {
    data.settings.forEach((item: any) => {
      const audience = item?.audience as Audience
      const chatId = Number(item?.chat_id)

      if (!AUDIENCES.includes(audience) || !chatId) {
        return
      }

      rows[audience].push(
        createChatRow(audience, {
          chatId,
          role: normalizeChatRole(item?.role),
          chat: { id: chatId, name: item?.chat?.name ?? `Чат #${chatId}` },
        }),
      )
    })

    return rows
  }

  // backend ещё не знает про settings: собираем строки из старых полей,
  // иначе сохранение из новой формы стёрло бы настроенные чаты.
  const legacy: [Audience, any[], unknown][] = [
    ['student', Array.isArray(data?.chats) ? data.chats : [], data?.student_role],
    ['parent', Array.isArray(data?.parent_chats) ? data.parent_chats : [], data?.parent_role],
  ]

  legacy.forEach(([audience, chats, role]) => {
    chats.forEach((chat) => {
      const chatId = Number(chat?.id)

      if (!chatId) {
        return
      }

      rows[audience].push(
        createChatRow(audience, {
          chatId,
          role: normalizeChatRole(role),
          chat: { id: chatId, name: chat?.name ?? `Чат #${chatId}` },
        }),
      )
    })
  })

  // Раньше сотрудники своих чатов не имели и ехали в чаты учащихся администраторами.
  rows.staff = rows.student.map((row) =>
    createChatRow('staff', { chatId: row.chatId, chat: row.chat, role: DEFAULT_CHAT_ROLE.staff }),
  )

  return rows
}

const ClassAutoChats: FC<ClassAutoChatsProps> = ({ classId }) => {
  const addAlert = useAddAlert()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [autoAddToChat, setAutoAddToChat] = useState(false)
  const [removeStudentsFromChatsOnDetach, setRemoveStudentsFromChatsOnDetach] =
    useState(false)
  const [autoAddParentsToChat, setAutoAddParentsToChat] = useState(false)
  const [
    removeParentsFromChatsOnDetach,
    setRemoveParentsFromChatsOnDetach,
  ] = useState(false)
  const [autoAddStaffToChat, setAutoAddStaffToChat] = useState(false)
  const [removeStaffFromChatsOnDetach, setRemoveStaffFromChatsOnDetach] =
    useState(false)
  const [rows, setRows] = useState<RowsByAudience>(EMPTY_ROWS)
  const [saveConfirmation, setSaveConfirmation] =
    useState<SaveConfirmation | null>(null)

  const applySettings = (data: any) => {
    setAutoAddToChat(Boolean(data?.auto_add_to_chat))
    setRemoveStudentsFromChatsOnDetach(
      Boolean(data?.remove_students_from_chats_on_detach),
    )
    setAutoAddParentsToChat(Boolean(data?.auto_add_parents_to_chat))
    setRemoveParentsFromChatsOnDetach(
      Boolean(data?.remove_parents_from_chats_on_detach),
    )
    setAutoAddStaffToChat(Boolean(data?.auto_add_staff_to_chat))
    setRemoveStaffFromChatsOnDetach(
      Boolean(data?.remove_staff_from_chats_on_detach),
    )
    setRows(parseRows(data))
  }

  const loadSettings = () => {
    if (!classId) return

    setLoading(true)

    classApi
      .getAutoChats(classId)
      .then((res) => {
        if (res?.data?.status !== 'success') {
          addAlert({
            type: 'error',
            title: 'Ошибка',
            text: 'Не удалось загрузить настройки чатов',
          })
          return
        }

        applySettings(res?.data?.data ?? {})
      })
      .catch(() => {
        addAlert({
          type: 'error',
          title: 'Ошибка',
          text: 'Не удалось загрузить настройки чатов',
        })
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadSettings()
  }, [classId])

  const filledRows = (audience: Audience) =>
    rows[audience].filter((row) => Boolean(row.chatId))

  const setAudienceRows = (audience: Audience, nextRows: ChatRow[]) =>
    setRows((current) => ({ ...current, [audience]: nextRows }))

  /** @returns удалось ли сохранить — по этому решается, показывать ли попап */
  const persistSettings = (
    addExistingStudents = false,
    addExistingParents = false,
  ): Promise<boolean> => {
    if (!classId) return Promise.resolve(false)

    setSaving(true)

    const settings = AUDIENCES.flatMap((audience) =>
      filledRows(audience).map((row) => ({
        audience,
        chat_id: row.chatId,
        role: row.role,
      })),
    )

    return classApi
      .syncAutoChats(classId, {
        auto_add_to_chat: autoAddToChat,
        remove_students_from_chats_on_detach: removeStudentsFromChatsOnDetach,
        auto_add_parents_to_chat: autoAddParentsToChat,
        remove_parents_from_chats_on_detach: removeParentsFromChatsOnDetach,
        auto_add_staff_to_chat: autoAddStaffToChat,
        remove_staff_from_chats_on_detach: removeStaffFromChatsOnDetach,
        settings,
        add_existing_students: addExistingStudents,
        add_existing_parents: addExistingParents,
      })
      .then((res) => {
        if (res?.data?.status !== 'success') {
          addAlert({
            type: 'error',
            title: 'Ошибка',
            text: 'Не удалось сохранить настройки чатов',
          })
          return false
        }

        applySettings(res?.data?.data ?? {})

        addAlert({
          type: 'success',
          title: 'Успешно',
          text:
            addExistingStudents || addExistingParents
              ? 'Настройки сохранены. Текущие участники добавятся в чаты в течение нескольких минут'
              : 'Настройки чатов класса сохранены',
        })

        return true
      })
      .catch((error) => {
        addAlert({
          type: 'error',
          title: 'Ошибка',
          text:
            error?.response?.data?.error ??
            error?.response?.data?.message ??
            'Не удалось сохранить настройки чатов',
        })

        return false
      })
      .finally(() => setSaving(false))
  }

  const save = () => {
    // Аудитории независимы: можно настроить только родителей, только сотрудников
    // или любую комбинацию. Секция без чатов просто ничего не делает, поэтому
    // сохранение она не блокирует.

    // Настройки применяются только к новым участникам, поэтому после каждого
    // сохранения спрашиваем отдельно про уже прикреплённый состав класса.
    const showStudents = autoAddToChat && filledRows('student').length > 0
    const showParents = autoAddParentsToChat && filledRows('parent').length > 0

    persistSettings().then((saved) => {
      if (!saved || (!showStudents && !showParents)) return

      setSaveConfirmation({
        showStudents,
        showParents,
        addExistingStudents: showStudents,
        addExistingParents: showParents,
      })
    })
  }

  if (!classId) {
    return null
  }

  return (
    <div className={classes.autoChatsCard}>
      <div className={classes.autoChatsHeader}>
        <p className={classes.autoChatsTitle}>Чаты класса</p>
        <div className={classes.tooltipWrapper}>
          <TooltipWrapper
            title="После сохранения ученики попадают в чаты не мгновенно, а через несколько минут"
            className={classes.tooltipMain}
            overlayClassName={classes.tooltipOverlay}
          >
            <InfoCircleOutlined
              className={classes.tooltipIcon}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          </TooltipWrapper>
        </div>
      </div>

      {loading ? (
        <div className={classes.autoChatsLoader}>
          <Preloader />
        </div>
      ) : (
        <>
          <section className={classes.autoChatsSection}>
            <div className={classes.autoChatsSectionHeader}>
              <p className={classes.autoChatsSectionTitle}>Учащиеся</p>
              <Checkbox
                className={cn(cclasses.checkbox, classes.checkbox)}
                checked={autoAddToChat}
                onChange={(e) => setAutoAddToChat(e.target.checked)}
              >
                Автоматически добавлять учащихся в чаты
              </Checkbox>
            </div>

            {autoAddToChat && (
              <ChatAudienceRows
                audience="student"
                chatLabel="Чат учащихся"
                rows={rows.student}
                onChange={(nextRows) => setAudienceRows('student', nextRows)}
              />
            )}

            <Checkbox
              className={cn(cclasses.checkbox, classes.checkbox)}
              checked={removeStudentsFromChatsOnDetach}
              onChange={(e) =>
                setRemoveStudentsFromChatsOnDetach(e.target.checked)
              }
            >
              Удалять учащегося из чатов при удалении из класса
            </Checkbox>
          </section>

          <section className={classes.autoChatsSection}>
            <div className={classes.autoChatsSectionHeader}>
              <p className={classes.autoChatsSectionTitle}>Родители</p>
              <Checkbox
                className={cn(cclasses.checkbox, classes.checkbox)}
                checked={autoAddParentsToChat}
                onChange={(e) => setAutoAddParentsToChat(e.target.checked)}
              >
                Автоматически добавлять родителей учащихся в чаты
              </Checkbox>
            </div>

            {autoAddParentsToChat && (
              <ChatAudienceRows
                audience="parent"
                chatLabel="Чат родителей"
                rows={rows.parent}
                onChange={(nextRows) => setAudienceRows('parent', nextRows)}
              />
            )}

            <Checkbox
              className={cn(cclasses.checkbox, classes.checkbox)}
              checked={removeParentsFromChatsOnDetach}
              onChange={(e) =>
                setRemoveParentsFromChatsOnDetach(e.target.checked)
              }
            >
              Удалять родителей из чатов при удалении ученика из класса
            </Checkbox>
          </section>

          <section className={classes.autoChatsSection}>
            <div className={classes.autoChatsSectionHeader}>
              <p className={classes.autoChatsSectionTitle}>Сотрудники</p>
              <Checkbox
                className={cn(cclasses.checkbox, classes.checkbox)}
                checked={autoAddStaffToChat}
                onChange={(e) => setAutoAddStaffToChat(e.target.checked)}
              >
                Добавлять сотрудников в чаты
              </Checkbox>
            </div>

            {autoAddStaffToChat && (
              <ChatAudienceRows
                audience="staff"
                chatLabel="Чат сотрудников"
                rows={rows.staff}
                onChange={(nextRows) => setAudienceRows('staff', nextRows)}
              />
            )}

            <Checkbox
              className={cn(cclasses.checkbox, classes.checkbox)}
              checked={removeStaffFromChatsOnDetach}
              onChange={(e) =>
                setRemoveStaffFromChatsOnDetach(e.target.checked)
              }
            >
              Удалять сотрудников из чатов
            </Checkbox>

            <p className={classes.autoChatsHint}>
              Сотрудники — педагоги групп класса.
            </p>
          </section>

          <p className={classes.autoChatsHint}>
            Настройки применяются к новым учащимся и их родителям. После
            сохранения можно отдельно подтвердить добавление уже прикреплённых
            участников класса.
          </p>

          <div className={classes.autoChatsActions}>
            <Button onClick={save} disabled={saving} isLoading={saving}>
              Сохранить
            </Button>
          </div>
        </>
      )}

      <Modal
        open={saveConfirmation !== null}
        centered
        title="Добавить текущих участников класса?"
        okText="Сохранить настройки"
        cancelText="Отменить"
        confirmLoading={saving}
        className={cn(jclasses.modal, classes.autoChatsConfirmationModal)}
        onCancel={() => !saving && setSaveConfirmation(null)}
        onOk={() => {
          if (!saveConfirmation) return

          // Ничего не отмечено — второй запрос не нужен, настройки уже сохранены.
          if (
            !saveConfirmation.addExistingStudents &&
            !saveConfirmation.addExistingParents
          ) {
            setSaveConfirmation(null)
            return
          }

          persistSettings(
            saveConfirmation.addExistingStudents,
            saveConfirmation.addExistingParents,
          ).then((saved) => {
            if (saved) setSaveConfirmation(null)
          })
        }}
      >
        <div className={classes.autoChatsConfirmationContent}>
          <p className={classes.autoChatsHint}>
            Настройки будут применяться к новым участникам. Выберите, кого также
            нужно добавить из текущего состава класса.
          </p>

          <div className={classes.autoChatsConfirmationOptions}>
            {saveConfirmation?.showStudents && (
              <Checkbox
                className={cn(cclasses.checkbox, classes.checkbox)}
                checked={saveConfirmation.addExistingStudents}
                onChange={(e) =>
                  setSaveConfirmation((current) =>
                    current
                      ? {
                          ...current,
                          addExistingStudents: e.target.checked,
                        }
                      : current,
                  )
                }
              >
                Добавить всех текущих учащихся класса в новые чаты
              </Checkbox>
            )}

            {saveConfirmation?.showParents && (
              <Checkbox
                className={cn(cclasses.checkbox, classes.checkbox)}
                checked={saveConfirmation.addExistingParents}
                onChange={(e) =>
                  setSaveConfirmation((current) =>
                    current
                      ? {
                          ...current,
                          addExistingParents: e.target.checked,
                        }
                      : current,
                  )
                }
              >
                Добавить родителей текущих учащихся класса в новые чаты
              </Checkbox>
            )}
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default ClassAutoChats
